import { AxiosResponse } from 'axios';
import ApiUrls from '../config/ApiUrls';
import { replaceByObject } from '../utils/Urls';
import API from './Api';
import { ApportionmentApiResponse } from './ApportionmentApi';
import { TransactionMatchApiResponse } from './CalculationApi';

export type RuleMatcher = {
  matchFieldType: string,
  matchType: string,
  values: string[],
};

export type AssignmentSettingApiResponse = {
  id: number,
  ncExternalId: string,
  trackingCategories: string[],
  apportionmentId: number,
  ignore: boolean,
};

export type RuleApiResponse = {
  id: number,
  ruleApportionmentId?: number,
  name: string,
  version?: string,
  organisationId: number,
  type: string,
  percentage: number,
  matchers: RuleMatcher[],
  applyAll: boolean,
  applyToUnmatched: boolean,
  nominalCode: string,
  trackingCategories: string[],
  apportionment?: ApportionmentApiResponse,
  assignmentSettings: AssignmentSettingApiResponse[],
  costHandler: string,
  transactionSource: string,
  fixedAmount: number,
  fixedAmountApplication: string,
};

export type RulesApiResponse = {
  rules: RuleApiResponse[],
  archivedRules: RuleApiResponse[],
};

interface UpdateMatchApiBody {
  doNotRecharge: boolean,
  overrideRuleApportionmentId?: number,
}

export class RuleApi extends API {
  static getRules(groupId: number, entityId: number): Promise<RulesApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.EntityRules, { groupId, entityId })
    ).then((response: AxiosResponse<RulesApiResponse>) => {
      return response.data;
    });
  }

  static getRule(groupId: number, ruleId: number): Promise<RuleApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.Rule, { groupId, ruleId })
    ).then((response: AxiosResponse<RuleApiResponse>) => response.data);
  }

  static getRuleApportionment(groupId: number, ruleApptId: number): Promise<RuleApiResponse> {
    return API.get(
      replaceByObject(ApiUrls.RuleApportionment, { groupId, ruleApptId })
    ).then((response: AxiosResponse<RuleApiResponse>) => response.data);
  }

  static archiveRule(groupId: number, entityId: number, ruleId: number): Promise<void> {
    return API.put(
      replaceByObject(ApiUrls.RuleArchive, { groupId, entityId, ruleId }),
      {}
    ).then((response: AxiosResponse<void>) => response.data);
  }

  static restoreRule(groupId: number, entityId: number, ruleId: number): Promise<void> {
    return API.put(
      replaceByObject(ApiUrls.RuleRestore, { groupId, entityId, ruleId }),
      {}
    ).then((response: AxiosResponse<void>) => response.data);
  }

  static updateMatch(groupId: number, calculationId: number, matchId: number, overrideRuleId: number | null, doNotRecharge: boolean): Promise<TransactionMatchApiResponse> {
    const body: UpdateMatchApiBody = { doNotRecharge };

    if (overrideRuleId !== null) {
      body.overrideRuleApportionmentId = overrideRuleId;
    }

    return API.put(
      replaceByObject(ApiUrls.TransactionMatch, { groupId, calculationId, matchId }),
      body
    ).then((response: AxiosResponse<TransactionMatchApiResponse>) => response.data);
  }

  static updateRule(groupId: number, entityId: number, ruleId: number, body: RuleApiResponse): Promise<RuleApiResponse> {
    return API.put(
      replaceByObject(ApiUrls.RuleUpdate, { groupId, entityId, ruleId }),
      body
    ).then((response: AxiosResponse<RuleApiResponse>) => response.data);
  }

  static createRule(groupId: number, entityId: number, body: RuleApiResponse): Promise<RuleApiResponse> {
    return API.post(
      replaceByObject(ApiUrls.EntityRules, { groupId, entityId }),
      body
    ).then((response: AxiosResponse<RuleApiResponse>) => response.data);
  }
}
